/**
 * XYPad — 2D touch surface (Kaoss-style). Sends two independent CCs,
 * one per axis. Puck follows the finger during touch; on release,
 * either stays at last position (default) or springs back to (64, 64)
 * when `returnToCenter: true`.
 *
 * Y axis inverted by default (up = higher value) — TouchOSC muscle
 * memory. Author can flip via Inspector.
 *
 * Values are 0..127 per axis. Drag fires both CCs at throttled ~60Hz.
 * Puck position + last values persist across reloads via the item's
 * xValue / yValue fields on the store.
 */

import { setLiveItemValue } from '../layout/projectStore.ts';
import { createSignal, createEffect, onCleanup, type Accessor } from 'solid-js';
import { fireAction, fanoutTargets } from '../opbridge/dispatch.ts';
import { ulid } from '../utils/ulid.ts';
import { editOwnsGesture, isEditMode, editItem, type XYPadItem, type LayoutColor } from '../layout/store.ts';
import { GUARD_GRAB_HOLD_MS, GUARD_GRAB_MOVE_TOL } from '../performance/store.ts';
import type { MidiCcStep } from '@opbuttons/schema';
import { noteControlValue } from '../timeline/recorder.ts';
import { releaseTouch } from '../timeline/touch.ts';
import { reportSend, controlName } from '../ui/pressReport.ts';
import type { FireResult } from '../opbridge/dispatch.ts';

interface XYPadProps {
  item: XYPadItem;
}

const COLOR_TOKEN: Record<LayoutColor, string> = {
  cyan: 'idle',
  magenta: 'incoming',
  amber: 'armed',
  green: 'ok',
};

const DISPATCH_INTERVAL_MS = 16;

export function XYPad(props: XYPadProps) {
  const [dragging, setDragging] = createSignal(false);
  // BUGFIX 2026-07-28 (same defect as Fader/Radial): the in-drag value
  // lives HERE, not in the store. A store write per move replaces the
  // item object, so App.tsx's `<For each={layoutItems()}>` recreates the
  // row, destroying this component and its pointer capture - the drag
  // then dies after one event and the pad sticks at its pointerdown
  // position. Persist once on release instead.
  const [liveXY, setLiveXY] = createSignal<{ x: number; y: number } | null>(null);
  const shownX = () => liveXY()?.x ?? props.item.xValue;
  const shownY = () => liveXY()?.y ?? props.item.yValue;
  let lastDispatchMs = 0;
  let pendingXY: { x: number; y: number } | null = null;
  let padEl: HTMLDivElement | undefined;

  // Show-mode guard (SD-5): a quick brush sends nothing; a long-press
  // "grabs" the pad (puck lights live) and then it tracks the drag.
  // Releasing re-locks — every drag session needs a fresh grab.
  const [grabbed, setGrabbed] = createSignal(false);
  let grabTimer: number | undefined;
  let grabStartX = 0;
  let grabStartY = 0;
  function beginPendingGrab(x: number, y: number): void {
    grabStartX = x;
    grabStartY = y;
    if (grabTimer !== undefined) clearTimeout(grabTimer);
    grabTimer = window.setTimeout(() => {
      grabTimer = undefined;
      setGrabbed(true);
      setDragging(true);
      const xy = xyFromPointer(grabStartX, grabStartY);
      commitXY(xy.x, xy.y);
    }, GUARD_GRAB_HOLD_MS);
  }
  function cancelPendingGrab(): void {
    if (grabTimer !== undefined) { clearTimeout(grabTimer); grabTimer = undefined; }
  }
  onCleanup(() => { if (grabTimer !== undefined) clearTimeout(grabTimer); });
  createEffect(() => { if (isEditMode()) { cancelPendingGrab(); setGrabbed(false); } });

  function tokenFor(): string {
    return COLOR_TOKEN[props.item.color];
  }

  /* Face flags - see the note in Fader.tsx. The XY pad is the third control
     with a readout and no name, so it had the same two inert toggles. */
  const name = () => props.item.name?.trim() ?? '';
  const showName = () => !props.item.hideName;
  const showInfo = () => !props.item.hideLabel;

  function xyFromPointer(clientX: number, clientY: number): { x: number; y: number } {
    if (!padEl) return { x: shownX(), y: shownY() };
    const rect = padEl.getBoundingClientRect();
    const xFrac = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    let yFrac = Math.max(0, Math.min(1, (clientY - rect.top) / rect.height));
    if (props.item.yInverted !== false) yFrac = 1 - yFrac;
    return {
      x: Math.round(xFrac * 127),
      y: Math.round(yFrac * 127),
    };
  }

  function dispatchXY(x: number, y: number): void {
    const now = performance.now();
    if (now - lastDispatchMs < DISPATCH_INTERVAL_MS) {
      pendingXY = { x, y };
      return;
    }
    lastDispatchMs = now;
    pendingXY = null;
    // Fan out both axes to every configured bridge (multi-select,
    // 2026-08-13) — undefined bridgeIds means the default bridge.
    const sent: Array<Promise<FireResult[]>> = [];
    for (const target of fanoutTargets(props.item.bridgeIds)) {
      const xStep: MidiCcStep = {
        type: 'midi.cc',
        id: ulid(),
        target,
        channel: props.item.xChannel + 1,
        cc: props.item.xCcNumber,
        value: x,
      };
      const yStep: MidiCcStep = {
        type: 'midi.cc',
        id: ulid(),
        target,
        channel: props.item.yChannel + 1,
        cc: props.item.yCcNumber,
        value: y,
      };
      sent.push(fireAction(xStep), fireAction(yStep));
    }
    void Promise.all(sent).then((r) => reportSend(props.item.id, controlName(props.item, 'XY pad'), r.flat()));
  }

  function commitXY(x: number, y: number): void {
    setLiveXY({ x, y });
    dispatchXY(x, y);
    noteControlValue(props.item.id, 'x', x);
    noteControlValue(props.item.id, 'y', y);
  }

  /** End of gesture: persist where the drag landed, hand display back to
   *  the store. */
  function persistXY(): void {
    const v = liveXY();
    if (v) setLiveItemValue(props.item.id, { xValue: v.x, yValue: v.y } as Partial<XYPadItem>);
    // The hand let go of both axes: a timeline holding either back takes it again per its touchMode (OPBridge S324).
    if (v) releaseTouch(props.item.bridgeIds, [{ channel: props.item.xChannel, cc: props.item.xCcNumber }, { channel: props.item.yChannel, cc: props.item.yCcNumber }]);
    setLiveXY(null);
  }

  function onPointerDown(e: PointerEvent & { currentTarget: HTMLDivElement }) {
    if (editOwnsGesture(props.item.id)) return;
    e.stopPropagation();
    e.preventDefault();
    const guarded = !isEditMode() && !!props.item.guard;
    if (guarded && !grabbed()) {
      e.currentTarget.setPointerCapture(e.pointerId);
      beginPendingGrab(e.clientX, e.clientY);
      return;
    }
    e.currentTarget.setPointerCapture(e.pointerId);
    setDragging(true);
    const xy = xyFromPointer(e.clientX, e.clientY);
    commitXY(xy.x, xy.y);
  }

  function onPointerMove(e: PointerEvent & { currentTarget: HTMLDivElement }) {
    if (grabTimer !== undefined) {
      if (Math.abs(e.clientX - grabStartX) > GUARD_GRAB_MOVE_TOL ||
          Math.abs(e.clientY - grabStartY) > GUARD_GRAB_MOVE_TOL) {
        cancelPendingGrab();
        try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* unsupported */ }
      }
      return;
    }
    if (!dragging()) return;
    e.stopPropagation();
    const xy = xyFromPointer(e.clientX, e.clientY);
    commitXY(xy.x, xy.y);
  }

  function onPointerUp(e: PointerEvent & { currentTarget: HTMLDivElement }) {
    if (grabTimer !== undefined) {
      cancelPendingGrab();
      try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* unsupported */ }
      return;
    }
    if (!dragging()) return;
    e.stopPropagation();
    e.currentTarget.releasePointerCapture(e.pointerId);
    setDragging(false);
    setGrabbed(false); // re-lock — a guarded pad needs a fresh grab next time
    // Flush pending value first so the final finger position lands even
    // if the last move was inside the throttle window.
    if (pendingXY) {
      lastDispatchMs = 0;
      commitXY(pendingXY.x, pendingXY.y);
    }
    if (props.item.returnToCenter) {
      lastDispatchMs = 0;
      commitXY(64, 64);
    }
    persistXY();
  }

  // Puck position as %, factoring y-invert so the visual matches what the
  // finger did.
  function puckXPct(): number {
    return (shownX() / 127) * 100;
  }
  function puckYPct(): number {
    const raw = (shownY() / 127) * 100;
    return props.item.yInverted !== false ? 100 - raw : raw;
  }

  return (
    <div
      class="xypad"
      data-token={tokenFor()}
      data-dragging={dragging() ? 'true' : 'false'}
      data-grabbed={grabbed() ? 'true' : 'false'}
      ref={padEl}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
    >
      <div class="xypad-crosshair xypad-crosshair-h" />
      <div class="xypad-crosshair xypad-crosshair-v" />
      <div
        class="xypad-puck"
        style={{
          left: `calc(${puckXPct()}% - 14px)`,
          top: `calc(${puckYPct()}% - 14px)`,
        }}
      />
      {showName() && name() && (
        <div class="xypad-name">{name()}</div>
      )}
      {showInfo() && (
        <div class="xypad-readout">
          <span>x cc {props.item.xCcNumber}·ch {props.item.xChannel + 1}</span>
          <span class="xypad-readout-value">{shownX()} · {shownY()}</span>
          <span>y cc {props.item.yCcNumber}·ch {props.item.yChannel + 1}</span>
        </div>
      )}
    </div>
  );
}
